// ---------------------------------------------------------------------------
// IndexedDB — Type-safe wrapper for offline mutation queue
// ---------------------------------------------------------------------------
// Used by the OfflineProvider to track queued mutations and sync status.
// The service worker also maintains its own IndexedDB store — this client-side
// wrapper provides a reactive interface for UI components.
// ---------------------------------------------------------------------------

const DB_NAME = 'hotelstack-offline';
const DB_VERSION = 2;
const QUEUE_STORE = 'mutation-queue';
const QUERY_CACHE_STORE = 'query-cache';

export interface QueuedMutation {
  id: string;
  url: string;
  method: string;
  headers: Record<string, string>;
  body: string;
  timestamp: number;
  retries: number;
  /** Human-readable label for the UI */
  label?: string;
  /** Module that created this mutation */
  module?: string;
  /** Idempotency key — same key replayed twice yields the same server outcome */
  idempotencyKey?: string;
  /** Operator id who authored this mutation */
  operatorId?: string;
}

export interface QueryCacheEntry {
  /** TanStack Query hash key */
  queryHash: string;
  /** Serialised query data */
  data: unknown;
  /** Last successful fetch timestamp (ms) */
  fetchedAt: number;
}

let dbInstance: IDBDatabase | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbInstance) return Promise.resolve(dbInstance);

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(QUEUE_STORE)) {
        db.createObjectStore(QUEUE_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(QUERY_CACHE_STORE)) {
        db.createObjectStore(QUERY_CACHE_STORE, { keyPath: 'queryHash' });
      }
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onerror = () => reject(request.error);
  });
}

export async function getQueuedMutations(): Promise<QueuedMutation[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(QUEUE_STORE, 'readonly');
    const req = tx.objectStore(QUEUE_STORE).getAll();
    req.onsuccess = () => resolve((req.result || []) as QueuedMutation[]);
    req.onerror = () => reject(req.error);
  });
}

export async function getQueueCount(): Promise<number> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(QUEUE_STORE, 'readonly');
    const req = tx.objectStore(QUEUE_STORE).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function saveQueuedMutation(mutation: QueuedMutation): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(QUEUE_STORE, 'readwrite');
    tx.objectStore(QUEUE_STORE).put(mutation);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function clearQueue(): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(QUEUE_STORE, 'readwrite');
    tx.objectStore(QUEUE_STORE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function removeQueuedMutation(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(QUEUE_STORE, 'readwrite');
    tx.objectStore(QUEUE_STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function saveQueryCacheEntry(entry: QueryCacheEntry): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(QUERY_CACHE_STORE, 'readwrite');
    tx.objectStore(QUERY_CACHE_STORE).put(entry);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getQueryCacheEntry(queryHash: string): Promise<QueryCacheEntry | undefined> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(QUERY_CACHE_STORE, 'readonly');
    const req = tx.objectStore(QUERY_CACHE_STORE).get(queryHash);
    req.onsuccess = () => resolve(req.result as QueryCacheEntry | undefined);
    req.onerror = () => reject(req.error);
  });
}

export async function getAllQueryCacheEntries(): Promise<QueryCacheEntry[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(QUERY_CACHE_STORE, 'readonly');
    const req = tx.objectStore(QUERY_CACHE_STORE).getAll();
    req.onsuccess = () => resolve((req.result || []) as QueryCacheEntry[]);
    req.onerror = () => reject(req.error);
  });
}

export async function clearQueryCache(): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(QUERY_CACHE_STORE, 'readwrite');
    tx.objectStore(QUERY_CACHE_STORE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
